// Enhanced Cart Management System for Shopify
class CartManager {
    constructor() {
        this.cart = window.cartData || null;
        this.routes = window.routes || {};
        this.moneyFormat = window.moneyFormat || '${{amount}}';
        this.currency = window.currency || { code: 'USD', symbol: '$' };
        this.init();
    }

    init() {
        this.bindEvents();
        this.loadCart();
        this.updateCartUI();
    }

    // Bind all event listeners
    bindEvents() {
        // Add to cart form (product pages) - Use more specific targeting
        document.addEventListener('submit', (e) => {
            if (e.target.matches('#add-to-cart-form') || e.target.closest('#add-to-cart-form')) {
                e.preventDefault(); // Prevent default form submission
                this.handleAddToCart(e);
            }
        });

        // Also bind to form directly when it exists
        const addToCartForm = document.getElementById('add-to-cart-form');
        if (addToCartForm) {
            addToCartForm.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleAddToCart(e);
            });
        }

        // Product page quantity controls
        this.bindQuantityControls();

        // Cart drawer controls
        this.bindCartDrawerEvents();

        // Variant selection
        this.bindVariantSelection();

        // Global cart icon click
        document.addEventListener('click', (e) => {
            if (e.target.closest('#open-cart')) {
                e.preventDefault();
                this.openCartDrawer();
            }
        });
    }

    // Bind quantity controls for product page
    bindQuantityControls() {
        document.addEventListener('click', (e) => {
            // Plus button
            if (e.target.closest('.qty-plus')) {
                e.preventDefault();
                const container = e.target.closest('form') || e.target.closest('.quantity-container');
                const visibleInput = container?.querySelector('.qty-input:not([type="hidden"])');
                const hiddenInput = container?.querySelector('input[name="quantity"][type="hidden"]');
                
                if (visibleInput) {
                    const currentValue = parseInt(visibleInput.value) || 1;
                    const newValue = currentValue + 1;
                    visibleInput.value = newValue;
                    if (hiddenInput) hiddenInput.value = newValue;
                    visibleInput.dispatchEvent(new Event('change'));
                }
            }

            // Minus button
            if (e.target.closest('.qty-minus')) {
                e.preventDefault();
                const container = e.target.closest('form') || e.target.closest('.quantity-container');
                const visibleInput = container?.querySelector('.qty-input:not([type="hidden"])');
                const hiddenInput = container?.querySelector('input[name="quantity"][type="hidden"]');
                
                if (visibleInput) {
                    const currentValue = parseInt(visibleInput.value) || 1;
                    if (currentValue > 1) {
                        const newValue = currentValue - 1;
                        visibleInput.value = newValue;
                        if (hiddenInput) hiddenInput.value = newValue;
                        visibleInput.dispatchEvent(new Event('change'));
                    }
                }
            }
        });

        // Quantity input change - only for visible inputs
        document.addEventListener('input', (e) => {
            if (e.target.matches('.qty-input:not([type="hidden"])') && !e.target.closest('#cart-items')) {
                const value = parseInt(e.target.value) || 1;
                if (value < 1) e.target.value = 1;
                
                const container = e.target.closest('form') || e.target.closest('.quantity-container');
                const hiddenInput = container?.querySelector('input[name="quantity"][type="hidden"]');
                if (hiddenInput) hiddenInput.value = e.target.value;
            }
        });
    }

    // Update hidden quantity input
    updateHiddenQuantity(container, value) {
        const hiddenQty = container?.querySelector('input[name="quantity"]');
        if (hiddenQty) hiddenQty.value = value;
    }

    // Bind variant selection
    bindVariantSelection() {
        document.addEventListener('change', (e) => {
            if (e.target.matches('input[name="model"]')) {
                const selectedVariant = e.target;
                const priceCell = selectedVariant.closest('tr')?.querySelector('.variant-price');
                const productPrice = document.querySelector('.product-price');
                
                if (priceCell && productPrice) {
                    productPrice.textContent = priceCell.textContent;
                }

                // Update hidden variant ID
                const variantIdInput = document.querySelector('input[name="id"]');
                if (variantIdInput) {
                    variantIdInput.value = selectedVariant.value;
                }
            }
        });
    }

    // Bind cart drawer events
    bindCartDrawerEvents() {
        // Close cart drawer
        document.addEventListener('click', (e) => {
            if (e.target.closest('#close-cart')) {
                this.closeCartDrawer();
            }

            // Close on backdrop click
            if (e.target.id === 'cart-backdrop') {
                this.closeCartDrawer();
            }

            // Remove item buttons
            if (e.target.closest('.remove-cart-item')) {
                e.preventDefault();
                const button = e.target.closest('.remove-cart-item');
                const itemKey = button.dataset.itemKey;
                this.removeFromCart(itemKey);
            }
        });

        // Cart quantity controls
        this.bindCartQuantityControls();

        // Escape key to close cart
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeCartDrawer();
            }
        });
    }

    // Bind cart quantity controls
    bindCartQuantityControls() {
        document.addEventListener('click', (e) => {
            const cartItem = e.target.closest('#cart-items li');
            if (!cartItem) return;

            const itemKey = cartItem.id.replace('cart-item-', '');
            const qtyInput = cartItem.querySelector('input[type="number"]');
            const currentQty = parseInt(qtyInput.value);

            if (e.target.closest('button')?.querySelector('[data-lucide="plus"]')) {
                this.updateCartItemQuantity(itemKey, currentQty + 1);
            } else if (e.target.closest('button')?.querySelector('[data-lucide="minus"]')) {
                if (currentQty > 1) {
                    this.updateCartItemQuantity(itemKey, currentQty - 1);
                }
            }
        });

        // Handle direct input changes in cart
        document.addEventListener('change', (e) => {
            if (e.target.matches('#cart-items input[type="number"]')) {
                const cartItem = e.target.closest('#cart-items li');
                const itemKey = cartItem.id.replace('cart-item-', '');
                const newQty = parseInt(e.target.value) || 1;
                
                if (newQty < 1) {
                    e.target.value = 1;
                    return;
                }
                
                this.updateCartItemQuantity(itemKey, newQty);
            }
        });
    }

    // Load current cart
    async loadCart() {
        try {
            const response = await fetch('/cart.js');
            this.cart = await response.json();
            this.updateCartUI();
        } catch (error) {
            console.error('Error loading cart:', error);
        }
    }

    // Handle add to cart form submission
    async handleAddToCart(e) {
        e.preventDefault();
        
        const form = e.target;
        const formData = new FormData(form);
        const submitButton = form.querySelector('button[type="submit"]');
        
        // Disable button and show loading state
        const originalContent = submitButton.innerHTML;
        submitButton.disabled = true;
        submitButton.classList.add('btn-loading');
        submitButton.innerHTML = '<span style="opacity: 0;">Adding...</span>';

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: formData.get('id'),
                    quantity: parseInt(formData.get('quantity'))
                })
            });

            if (response.ok) {
                const addedItem = await response.json();
                await this.loadCart();
                this.openCartDrawer();
                this.showSuccessMessage(`${addedItem.product_title} added to cart!`);
            } else {
                const error = await response.json();
                this.showErrorMessage(error.description || 'Error adding product to cart');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            this.showErrorMessage('Error adding product to cart');
        } finally {
            // Re-enable button
            submitButton.disabled = false;
            submitButton.classList.remove('btn-loading');
            submitButton.innerHTML = originalContent;
        }
    }

    // Update cart item quantity
    async updateCartItemQuantity(itemKey, quantity) {
        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: itemKey,
                    quantity: quantity
                })
            });

            if (response.ok) {
                await this.loadCart();
            } else {
                console.error('Error updating quantity');
                this.showErrorMessage('Error updating quantity');
            }
        } catch (error) {
            console.error('Error updating cart:', error);
            this.showErrorMessage('Error updating cart');
        }
    }

    // Remove item from cart
    async removeFromCart(itemKey) {
        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: itemKey,
                    quantity: 0
                })
            });

            if (response.ok) {
                await this.loadCart();
                this.showSuccessMessage('Item removed from cart');
            } else {
                console.error('Error removing item');
                this.showErrorMessage('Error removing item');
            }
        } catch (error) {
            console.error('Error removing from cart:', error);
            this.showErrorMessage('Error removing item');
        }
    }

    // Update cart UI
    updateCartUI() {
        this.updateCartCount();
        this.updateCartDrawer();
        this.updateCheckoutButton();
    }

    // Update cart count in header
    updateCartCount() {
        const cartButtons = document.querySelectorAll('#open-cart, [data-cart-count]');
        const itemCount = this.cart?.item_count || 0;
        
        cartButtons.forEach(button => {
            const countSpan = button.querySelector('span');
            if (countSpan) {
                countSpan.textContent = itemCount > 0 ? `(${itemCount})` : '';
            }
        });
    }

    // Update checkout button state
    updateCheckoutButton() {
        const checkoutBtn = document.getElementById('checkout-btn');
        if (checkoutBtn) {
            const isEmpty = !this.cart || this.cart.item_count === 0;
            checkoutBtn.disabled = isEmpty;
            checkoutBtn.textContent = isEmpty ? 'Cart is Empty' : 'Proceed to Checkout';
        }
    }

    // Update cart drawer content
    updateCartDrawer() {
        const cartItems = document.getElementById('cart-items');
        const cartSummary = document.getElementById('cart-summary');

        if (!cartItems || !cartSummary) return;

        // Update cart items
        if (!this.cart || this.cart.item_count === 0) {
            cartItems.innerHTML = '<li class="text-center text-sm text-gray-600 py-8">Your cart is empty.</li>';
        } else {
            cartItems.innerHTML = this.cart.items.map((item, index) => this.renderCartItem(item, index)).join('');
        }

        // Update cart summary
        cartSummary.innerHTML = this.renderCartSummary();

        // Re-initialize Lucide icons if available
        if (window.lucide) {
            window.lucide.createIcons();
        }
    }

    // Render cart item HTML
    renderCartItem(item, index) {
        const imageUrl = item.featured_image?.url || item.image;
        
        return `
            <li class="flex items-start justify-between border border-border md:p-[18px] p-3" id="cart-item-${item.key}">
                <div class="bg-card-secondary-bg md:h-[140px] md:w-[140px] w-20 h-20 flex items-center justify-center">
                    ${imageUrl ? `<img src="${imageUrl}" alt="${item.title}" class="w-full h-full object-contain" />` : '<div class="w-full h-full bg-gray-200"></div>'}
                </div>
                <div class="flex-1 ml-4">
                    <div class="flex justify-between">
                        <div>
                            <h3 class="md:text-lg text-sm font-medium mb-1">${item.product_title}</h3>
                            ${item.variant_title ? `<p class="md:text-sm text-xs text-text-muted md:mb-2 mb-1">${item.variant_title}</p>` : ''}
                            <a href="${item.url}" class="md:text-sm text-xs underline">View Details</a>
                        </div>
                        <button class="text-secondary remove-cart-item" data-item-key="${item.key}" data-line="${index + 1}" data-item-qty="${item.quantity}">
                            <i data-lucide="trash" stroke-width="1.5" class="md:w-6 md:h-6 w-4 h-4"></i>
                        </button>
                    </div>

                    <div class="flex md:items-center gap-2 justify-between md:mt-6 mt-4">
                        <div class="flex items-center">
                            <button type="button" class="md:w-8 md:h-8 w-6 h-6 border border-border flex items-center justify-center hover:bg-card-bg">
                                <i data-lucide="minus" stroke-width="1.5" class="md:w-4 md:h-4 w-3 h-3"></i>
                            </button>
                            <input type="number" value="${item.quantity}" min="1" 
                                    class="md:w-8 md:h-8 w-6 h-6 text-center border-t border-b border-border text-xs font-medium focus:outline-none" />
                            <button type="button" class="md:w-8 md:h-8 w-6 h-6 border border-border flex items-center justify-center hover:bg-card-bg">
                                <i data-lucide="plus" stroke-width="1.5" class="md:w-4 md:h-4 w-3 h-3"></i>
                            </button>
                        </div>
                        <p class="md:text-xl text-base font-medium">${this.formatMoney(item.final_price)}</p>
                    </div>
                </div>
            </li>
        `;
    }

    // Render cart summary HTML
    renderCartSummary() {
        const subtotal = this.cart?.total_price || 0;
        const tax = this.cart?.total_tax || 0;
        const total = subtotal;

        return `
            <li class="flex justify-between">
                <span>Sub Total</span>
                <span>${this.formatMoney(subtotal)}</span>
            </li>
            <li class="flex justify-between">
                <span>Tax</span>
                <span>${this.formatMoney(tax)}</span>
            </li>
            <li class="flex justify-between font-medium md:text-lg text-base border-t border-border pt-2">
                <span>Total Amount</span>
                <span>${this.formatMoney(total)}</span>
            </li>
        `;
    }

    // Format money using Shopify's format
    formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        return this.moneyFormat.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100).toString());
    }

    // Open cart drawer
    openCartDrawer() {
        const cartDrawer = document.getElementById('cart-drawer');
        const cartBackdrop = document.getElementById('cart-backdrop');
        
        if (cartDrawer) {
            cartDrawer.classList.remove('translate-x-full');
            document.body.style.overflow = 'hidden';
            
            if (cartBackdrop) {
                cartBackdrop.classList.remove('opacity-0', 'invisible');
            }
        }
    }

    // Close cart drawer
    closeCartDrawer() {
        const cartDrawer = document.getElementById('cart-drawer');
        const cartBackdrop = document.getElementById('cart-backdrop');
        
        if (cartDrawer) {
            cartDrawer.classList.add('translate-x-full');
            document.body.style.overflow = '';
            
            if (cartBackdrop) {
                cartBackdrop.classList.add('opacity-0', 'invisible');
            }
        }
    }

    // Show success message
    showSuccessMessage(message) {
        this.showMessage(message, 'success');
    }

    // Show error message
    showErrorMessage(message) {
        this.showMessage(message, 'error');
    }

    // Show message notification
    showMessage(message, type = 'success') {
        const toast = document.createElement('div');
        toast.className = `toast-notification fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${
            type === 'success' 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
        }`;
        
        toast.innerHTML = `
            <div class="flex items-center justify-between">
                <span>${message}</span>
                <button onclick="this.parentElement.parentElement.remove()" class="ml-2 text-white opacity-70 hover:opacity-100">
                    <i data-lucide="x" stroke-width="2" class="w-4 h-4"></i>
                </button>
            </div>
        `;
        
        document.body.appendChild(toast);
        
        // Initialize icons for the toast
        if (window.lucide) {
            window.lucide.createIcons();
        }
        
        // Auto-remove toast after 5 seconds
        setTimeout(() => {
            if (toast.parentElement) {
                toast.style.opacity = '0';
                toast.style.transform = 'translateX(100%)';
                setTimeout(() => {
                    if (toast.parentElement) {
                        toast.remove();
                    }
                }, 300);
            }
        }, 5000);
    }
}

// Initialize cart manager when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    window.cartManager = new CartManager();
});

// Make it available globally
window.CartManager = CartManager;